import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { SnapshotRestoreCoordinator } from '../../../../services/snapshot-restore-coordinator.service';
import { TmsNotificationService } from '../../../../services/tms-notification.service';
import { TsmAppFormService } from '../../../tsm-app/services/tsm-app-form.service';
import { TmsLabelService } from '../../../../language/services/tms-label.service';
import {
	isFormExpiredError,
	isEditAtRiskMode,
	closeFormQuietly,
	restoreFormSnapshot,
	retryOnFormExpired,
	ReBootstrapGuard,
} from '../../../../shared/form-lifecycle';
import {
	ActionItem,
	DeltaEntry,
	DeltaResponse,
	EditPaneSection,
	FieldState,
	ToolbarDescriptor,
	TsmMode,
	UpdateBody,
	isInsertLikeMode,
} from '../../models/ref-app.model';
import {
	ApplyRefDeltas,
	BootstrapRefForm,
	ClearRefAppState,
	CloseRefForm,
	InitRefAppState,
	OnRefAdaptorChange,
	OnRefAdaptorChangeBatch,
	PickRefRecord,
	RebindRefForm,
	RestoreRefAppSnapshot,
	RunRefAppCommand,
	SaveRefForm,
} from '../actions/ref-app.actions';

/**
 * REF Phase 2 (v2) — NGXS state slice owning the REF edit-form lifecycle.
 * Mirrors {@code TsmAppState}'s edit-form slices ({@code formId}, {@code mode},
 * {@code primaryKeys}, {@code fields}, {@code panes}, {@code actions},
 * {@code lastSaveResponse}, {@code matrixReloadCounters}) plus REF-specific
 * {@code selfLookupName}/{@code filterLookupFlag} populated from the bootstrap
 * response.
 *
 * <p>Listing concerns (filterMetadata, viz, profiles, columnVisibility,
 * filterSelections, etc.) are intentionally NOT here — REF has no listing
 * page. See tms-ref-apps.md §5.3 for the model definition and §20.6 of v1 for
 * the rationale behind the parallel-not-shared decision.
 */

// Server-implicit tenant-scope adaptor (AppData.TENANT_CD_ADAP). The
// lookup-suggest per-row PK map includes it (resolved from the authenticated
// request); it is NOT a form field the user sets, so pick-driven field
// updates exclude it — and the loaded-record pill label (ref-app-page) hides
// it too. Exported as the single definition of the tenant adaptor name.
export const TENANT_CD_ADAPTOR: string = 'tenant_cd';

export class RefAppStateModel {
	// Identity
	appName!: string;
	appTitle!: string;

	// Edit-form lifecycle (mirrors TsmAppState; same semantics + same FORM_EXPIRED retry path)
	formId!: string | null;
	mode!: TsmMode | null;
	primaryKeys!: Record<string, string> | null;
	// Pill label override, set at PickRefRecord time. Null for ordinary
	// self-lookups (the pill joins primaryKeys as before). Non-null when the
	// server's primaryKeyFields[] (minus tenant_cd) can't identify the record
	// — then primaryKeys holds the FULL legacy write-back projection (names,
	// dates) and joining it reads like noise; the picked row's first display
	// value (its code column) is the identity instead. See ref-app-page
	// loadedLabel.
	recordLabelHint!: string | null;
	fields!: Record<string, FieldState>;
	panes!: EditPaneSection[];
	actions!: ActionItem[];
	lastSaveResponse!: DeltaResponse | null;
	matrixReloadCounters!: Readonly<Record<string, number>>;
	// Toolbar-migration — server-projected toolbar descriptor from the
	// bootstrap response (same emission path as TSM); drives the page header's
	// migrated toolbar buttons. Null until a bootstrap response carries one.
	toolbar!: ToolbarDescriptor | null;

	// REF-specific — server emits these on bootstrap; consumed by the page
	// shell (search-pill magnifier) and the self-lookup overlay.
	selfLookupName!: string | null;
	filterLookupFlag!: boolean;

	// UX flags
	loading!: boolean;
	error!: string | null;

	// True while a RestoreRefAppSnapshot is mid-flight; drives the REF detail
	// skeleton loader. See impl doc §4.5 / §5.2.
	restoreInFlight!: boolean;
	// Path A vestigial — see TsmAppState.touchSequenceCounter for the
	// rationale on keeping the seed counter despite Path B not capturing
	// dirty fields client-side. Per-field {@code touchedSequence} still
	// drives in-session UI hints.
	touchSequenceCounter!: number;
}

const DEFAULT_REF_APP_STATE: RefAppStateModel = {
	appName: '',
	appTitle: '',
	formId: null,
	mode: null,
	primaryKeys: null,
	recordLabelHint: null,
	fields: {},
	panes: [],
	actions: [],
	lastSaveResponse: null,
	matrixReloadCounters: {},
	toolbar: null,
	selfLookupName: null,
	filterLookupFlag: false,
	loading: false,
	error: null,
	restoreInFlight: false,
	touchSequenceCounter: 0,
};

@State<RefAppStateModel>({
	name: 'refApp',
	defaults: DEFAULT_REF_APP_STATE,
})
@Injectable()
export class RefAppState {
	@Selector() static appName(state: RefAppStateModel): string {
		return state.appName;
	}
	@Selector() static appTitle(state: RefAppStateModel): string {
		return state.appTitle;
	}
	@Selector() static formId(state: RefAppStateModel): string | null {
		return state.formId;
	}
	@Selector() static mode(state: RefAppStateModel): TsmMode | null {
		return state.mode;
	}
	@Selector() static primaryKeys(state: RefAppStateModel): Record<string, string> | null {
		return state.primaryKeys;
	}
	@Selector() static recordLabelHint(state: RefAppStateModel): string | null {
		return state.recordLabelHint;
	}
	@Selector() static fields(state: RefAppStateModel): Record<string, FieldState> {
		return state.fields;
	}
	@Selector() static panes(state: RefAppStateModel): EditPaneSection[] {
		return state.panes;
	}
	@Selector() static actions(state: RefAppStateModel): ActionItem[] {
		return state.actions;
	}
	@Selector() static lastSaveResponse(state: RefAppStateModel): DeltaResponse | null {
		return state.lastSaveResponse;
	}
	@Selector() static matrixReloadCounters(state: RefAppStateModel): Readonly<Record<string, number>> {
		return state.matrixReloadCounters;
	}
	// Toolbar-migration — server-projected toolbar for the page
	// header's migrated buttons. Null until the bootstrap response carries one.
	@Selector() static toolbar(state: RefAppStateModel): ToolbarDescriptor | null {
		return state.toolbar;
	}
	@Selector() static selfLookupName(state: RefAppStateModel): string | null {
		return state.selfLookupName;
	}
	@Selector() static filterLookupFlag(state: RefAppStateModel): boolean {
		return state.filterLookupFlag;
	}
	@Selector() static loading(state: RefAppStateModel): boolean {
		return state.loading;
	}
	@Selector() static error(state: RefAppStateModel): string | null {
		return state.error;
	}
	// TMS Navigation — page-state preservation. True while a snapshot restore
	// is mid-flight; consumed by the REF detail skeleton loader.
	@Selector() static restoreInFlight(state: RefAppStateModel): boolean {
		return state.restoreInFlight;
	}
	constructor(
		private formService: TsmAppFormService,
		private notify: TmsNotificationService,
		private coordinator: SnapshotRestoreCoordinator,
		private labels: TmsLabelService,
	) {}

	// -- Init / clear --

	@Action(InitRefAppState)
	initRefAppState(ctx: StateContext<RefAppStateModel>, action: InitRefAppState): void {
		// Release (null — do NOT close) any prior app's formId. InitRefAppState runs
		// only on cross-app navigation / first mount (the page's paramMap appName
		// change), where Path B has already snapshotted the outgoing app's form for
		// return-nav. Leaving formId set let the upcoming BootstrapRefForm's
		// close-prior-form guard evict that snapshotted form — so navigating REF→REF
		// and back 410'd ("previous session expired") on a fresh session. Same-app
		// mode swaps / record picks do NOT run InitRefAppState, so they keep formId
		// and still close the replaced form (no leak). The released form is reclaimed
		// by the server's idle TTL if the user never navigates back.
		ctx.patchState({ appName: action.appName, error: null, formId: null });
	}

	@Action(ClearRefAppState)
	clearRefAppState(ctx: StateContext<RefAppStateModel>): void {
		ctx.setState(DEFAULT_REF_APP_STATE);
	}

	// -- Edit-form lifecycle (shared SSoT — see shared/form-lifecycle) --

	/** Fetch-first + concurrency-guarded re-bootstrap (shared SSoT — one instance per
	 *  NGXS state singleton). See {@link ReBootstrapGuard}. */
	private readonly reBootstrap: ReBootstrapGuard = new ReBootstrapGuard();

	@Action(BootstrapRefForm)
	async bootstrapRefForm(ctx: StateContext<RefAppStateModel>, action: BootstrapRefForm): Promise<void> {
		const prior: RefAppStateModel = ctx.getState();
		const idempotencyKey: string = crypto.randomUUID();
		const body: UpdateBody = {
			idempotencyKey,
			app: prior.appName,
			mode: action.mode,
			primaryKeys: action.primaryKeys,
		};
		// Fetch-first via the shared guard: on failure the prior form stays rendered
		// (never blank); only the latest re-bootstrap applies. apply() runs only on a
		// successful, still-latest fetch — close prior form (D-8 leak-avoidance, now
		// AFTER success), reset, dispatch deltas.
		await this.reBootstrap.run<DeltaResponse>({
			fetch: () => firstValueFrom(this.formService.update(body)),
			logTag: '[BootstrapRefForm]',
			apply: async (response: DeltaResponse): Promise<void> => {
				await closeFormQuietly(this.formService, prior.formId, '[BootstrapRefForm]');
				ctx.patchState({
					mode: action.mode,
					primaryKeys: action.primaryKeys ?? null,
					// Written atomically with primaryKeys — the ONLY writer besides
					// rebindRefForm. Prevents a stale hint from a previous record /
					// previous REF app surviving a non-pick load (nav ?openDetailPk,
					// mode change, default bootstrap). See BootstrapRefForm.labelHint.
					recordLabelHint: action.labelHint ?? null,
					fields: {},
					panes: [],
					actions: [],
					matrixReloadCounters: {},
				});
				ctx.dispatch(new ApplyRefDeltas(response));
			},
		});
	}

	/**
	 * REF mirror of {@code TsmAppState.rebindForm}. Re-binds REF state to a
	 * still-cached server-side {@code BackEndAppProcessor} by formId.
	 * Server emits the full bootstrap envelope read from the cached BAP's
	 * AppData — user's in-flight typed values + matrix in-memory rows
	 * survive verbatim. On 410 the orchestrator catches and falls back to
	 * {@link BootstrapRefForm}.
	 */
	@Action(RebindRefForm)
	async rebindRefForm(ctx: StateContext<RefAppStateModel>, action: RebindRefForm): Promise<void> {
		const idempotencyKey: string = crypto.randomUUID();
		const response: DeltaResponse = await firstValueFrom(
			this.formService.rebind(action.formId, idempotencyKey),
		);
		// {@code mode} + {@code primaryKeys} come from the snapshot — the
		// rebind response carries deltas + panes only, not record identity,
		// and downstream consumers (matrix host's load API, detail title)
		// need both to render correctly.
		ctx.patchState({
			formId: action.formId,
			mode: action.mode,
			primaryKeys: action.primaryKeys,
			// Atomic with primaryKeys — see bootstrapRefForm / BootstrapRefForm.labelHint.
			recordLabelHint: action.labelHint ?? null,
			fields: {},
			panes: [],
			actions: [],
			matrixReloadCounters: {},
		});
		ctx.dispatch(new ApplyRefDeltas(response));
	}

	@Action(ApplyRefDeltas)
	applyRefDeltas(ctx: StateContext<RefAppStateModel>, action: ApplyRefDeltas): void {
		const state: RefAppStateModel = ctx.getState();
		const updates: Record<string, FieldState> = { ...state.fields };

		// Phase 3 audit MF-3 + Phase 4 (TSM parity): clear stale field errors only
		// when the response carries a save outcome (statusMessage or messages[]).
		// Field-update-only responses leave prior errors UNTOUCHED — editing field B
		// shouldn't wipe a validation error highlight on field A.
		const hasSaveOutcome: boolean =
			action.response.statusMessage !== undefined
			|| (action.response.messages !== undefined && action.response.messages.length > 0);
		if (hasSaveOutcome) {
			for (const adaptor of Object.keys(updates)) {
				if (updates[adaptor].error !== undefined) {
					const cleared: FieldState = { ...updates[adaptor] };
					delete cleared.error;
					updates[adaptor] = cleared;
				}
			}
		}

		let nextMode: TsmMode | null = state.mode;
		for (const delta of action.response.deltas as DeltaEntry[]) {
			// TMS Navigation — preserve prior `touchedSequence` through
			// server-response merge (mirror of TsmAppState.applyDeltas).
			// Bootstrap resets `fields:{}` first so prior is undefined →
			// touchedSequence=null. Mid-form edits land here with the
			// optimistic-patch sequence already assigned.
			const prior: FieldState | undefined = updates[delta.adaptor];
			updates[delta.adaptor] = {
				value: delta.value,
				editable: delta.editable,
				visible: delta.visible,
				color: delta.color,
				description: delta.description,
				// Navigability is a field property, not a function of the current value —
				// a value-change delta that omits `nav` must NOT clear an existing target.
				// Preserve prior nav when the delta carries none (mirrors TsmAppState).
				nav: delta.nav ?? prior?.nav,
				touchedSequence: prior?.touchedSequence ?? null,
			};
			if (delta.adaptor === 'mode' && typeof delta.value === 'string') {
				nextMode = delta.value as TsmMode;
			}
		}

		// Populate per-field errors from save-time validation messages.
		const messages: ReadonlyArray<{ type: 'error' | 'warning' | 'info'; field?: string; text: string }> =
			action.response.messages ?? [];
		for (const msg of messages) {
			if (msg.type !== 'error') continue;
			if (msg.field) {
				const existing: FieldState = updates[msg.field] ?? {
					value: null, editable: true, visible: true, color: null, touchedSequence: null,
				};
				updates[msg.field] = { ...existing, error: msg.text };
			} else {
				// Cross-field (no per-adaptor anchor) validation errors aren't
				// attachable to a single field; surface them via snackbar so
				// the user sees the server's complaint. The legacy ATOM
				// AppendSystemMessage action isn't wired into RefAppState today.
				console.warn(`Cross-field validation error: ${msg.text}`);
				// messages[] entries (msg.text) are NOT captured by the global
				// TmsMessageInterceptor — record them to history here.
				this.notify.error(msg.text);
			}
		}

		// panes[] arrives only on bootstrap (lock #16); field-update + save
		// responses omit it. Use panes-presence as the bootstrap discriminator
		// for ALL app-shell state (panes, metadata, selfLookupName,
		// filterLookupFlag, AND actions) — otherwise a save response that
		// returns a stripped-down actions list (e.g. delete returns just
		// [Inquire]) clobbers the full mode menu the user needs to keep
		// using the app. Field-update / save responses leave actions UNTOUCHED;
		// the bootstrap snapshot remains authoritative for the mode menu.
		const patch: Partial<RefAppStateModel> = {
			formId: action.response.formId,
			mode: nextMode,
			fields: updates,
		};
		if (action.response.panes !== undefined) {
			patch.panes = action.response.panes;
			patch.actions = action.response.actions;
			// appTitle: prefer the resolved title; fall back to the app code
			// (metadata.name) when the app carries no title, so a title-less ref
			// app still clears the breadcrumb's labelPending gate (mirrors TSM's
			// appName fallback) instead of hiding the chip. Guarded on metadata
			// presence — only set once the bootstrap has returned.
			if (action.response.metadata) {
				patch.appTitle = action.response.metadata.title || action.response.metadata.name;
			}
			if (action.response.selfLookupName !== undefined) {
				patch.selfLookupName = action.response.selfLookupName || null;
			}
			if (action.response.filterLookupFlag !== undefined) {
				patch.filterLookupFlag = action.response.filterLookupFlag;
			}
		}
		// Toolbar-migration — the server-projected toolbar arrives only
		// on the bootstrap response. Patch only when present so field-update /
		// save responses (which omit it) leave the cached toolbar untouched
		// rather than nulling the header's buttons mid-edit (mirrors
		// TsmAppState.applyDeltas).
		if (action.response.toolbar !== undefined) {
			patch.toolbar = action.response.toolbar;
		}
		if (action.response.matricesChanged?.length) {
			const next: Record<string, number> = { ...state.matrixReloadCounters };
			for (const name of action.response.matricesChanged) {
				next[name] = (next[name] ?? 0) + 1;
			}
			patch.matrixReloadCounters = next;
		}

		// TMS Navigation — page-state preservation. Refresh the persistence
		// baseline + reset touchedSequence on the two persistence points
		// (bootstrap and save). Counter also resets to 0 so the next edit
		// cycle starts at sequence 1. Mirror of TsmAppState.applyDeltas —
		// see that comment for rationale.
		const isBootstrap: boolean = action.response.panes !== undefined;
		const isSaveSuccess: boolean = action.response.statusMessage !== undefined && action.response.statusMessage.length > 0;
		if (isBootstrap || isSaveSuccess) {
			const cleared: Record<string, FieldState> = {};
			for (const [adaptor, fs] of Object.entries(updates)) {
				cleared[adaptor] = fs.touchedSequence !== null ? { ...fs, touchedSequence: null } : fs;
			}
			patch.fields = cleared;
			patch.touchSequenceCounter = 0;
		}

		ctx.patchState(patch);

		// Surface observer-set STATUS_MESSAGE (e.g. SelectRecordObserver's
		// "Found the Record!" on a record-pick bootstrap) as a green transient
		// snackbar — same pattern the save handler uses for "Saved" / etc. Gated
		// on `panes !== undefined` so this only fires on the BOOTSTRAP response
		// path (panes are emitted only on bootstrap per spec lock #16); save
		// responses run through their own page-level snackbar in onSaveClick.
		// NOTE: this is a registered consumer of the message-announce policy
		// (services/tms-message-policy.ts). REF has no quiet re-read today, so
		// there is no suppression fact to pass — when one lands (e.g. a REF
		// post-save re-read), gate this toast through shouldAnnounceSuccess()
		// like the TSM ApplyDeltas toast and the message interceptor do.
		const statusMessage: string | undefined = action.response.statusMessage;
		if (statusMessage !== undefined && statusMessage.length > 0 && action.response.panes !== undefined) {
			// statusMessage is the server response field — already recorded to
			// history by the global TmsMessageInterceptor; toast-only here.
			this.notify.toast('success', statusMessage);
		}
	}

	@Action(OnRefAdaptorChange)
	async onRefAdaptorChange(ctx: StateContext<RefAppStateModel>, action: OnRefAdaptorChange): Promise<void> {
		await firstValueFrom(ctx.dispatch(new OnRefAdaptorChangeBatch({ [action.adaptor]: action.value })));
	}

	/**
	 * REF edit-mode field update. Optimistic patch → POST → apply deltas, with
	 * FORM_EXPIRED retry and rollback on transport failure. Mirrors TSM's
	 * onAdaptorChangeBatch detail-context branch.
	 */
	@Action(OnRefAdaptorChangeBatch)
	async onRefAdaptorChangeBatch(
		ctx: StateContext<RefAppStateModel>,
		action: OnRefAdaptorChangeBatch,
	): Promise<void> {
		const state: RefAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('OnRefAdaptorChangeBatch dispatched with no formId — BootstrapRefForm must run first');
		}

		// Step 1 — optimistic snapshot + patch
		// TMS Navigation — page-state preservation: assign a first-edit
		// {@code touchedSequence} per adaptor in this batch. Counter is shared
		// across all edits in the current persistence cycle. Subsequent edits
		// to a field that already has a sequence LEAVE IT UNCHANGED (first-edit
		// ordering — impl doc §8.1). Mirror of TsmAppState detail branch.
		const fieldsSnapshot: Record<string, FieldState | undefined> = {};
		const fields: Record<string, FieldState> = { ...state.fields };
		let counter: number = state.touchSequenceCounter;
		for (const [adaptor, value] of Object.entries(action.fields)) {
			fieldsSnapshot[adaptor] = state.fields[adaptor];
			const existing: FieldState | undefined = state.fields[adaptor];
			const existingSequence: number | null = existing?.touchedSequence ?? null;
			let nextSequence: number | null;
			if (existingSequence === null) {
				counter += 1;
				nextSequence = counter;
			} else {
				nextSequence = existingSequence;
			}
			fields[adaptor] = {
				...(existing ?? { value: null, editable: true, visible: true, color: null, touchedSequence: null }),
				value,
				touchedSequence: nextSequence,
			};
		}
		ctx.patchState({ fields, touchSequenceCounter: counter });

		// Step 2 — POST
		const idempotencyKey: string = crypto.randomUUID();
		const body: UpdateBody = {
			idempotencyKey,
			formId: state.formId,
			fields: action.fields,
		};

		try {
			const response: DeltaResponse = await firstValueFrom(this.formService.update(body));
			ctx.dispatch(new ApplyRefDeltas(response));
		} catch (err: unknown) {
			// Rollback the optimistic patch on any error path.
			const rollback: Record<string, FieldState> = { ...ctx.getState().fields };
			for (const [adaptor, snapshot] of Object.entries(fieldsSnapshot)) {
				if (snapshot === undefined) {
					delete rollback[adaptor];
				} else {
					rollback[adaptor] = snapshot;
				}
			}
			ctx.patchState({ fields: rollback });

			// FORM_EXPIRED — re-bootstrap with cached mode/primaryKeys then replay
			// (shared SSoT). The mode guard + rollback above stay REF-specific.
			if (isFormExpiredError(err) && !state.mode) {
				throw new Error('FORM_EXPIRED retry requires state.mode; missing');
			}
			await retryOnFormExpired(err, {
				// Same record re-loads → carry the current pill hint through.
				reBootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapRefForm(
					state.mode!, state.primaryKeys ?? undefined, state.recordLabelHint))),
				replay: () => firstValueFrom(ctx.dispatch(new OnRefAdaptorChangeBatch(action.fields))),
			});
		}
	}

	@Action(SaveRefForm)
	async saveRefForm(ctx: StateContext<RefAppStateModel>): Promise<void> {
		const state: RefAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('SaveRefForm dispatched with no formId — BootstrapRefForm must run first');
		}
		// Capture the mode BEFORE save so we know how to update the loaded-
		// record indicators after the deltas merge (delete clears them,
		// update refreshes the description values).
		const modeBeforeSave: TsmMode | null = state.mode;
		const idempotencyKey: string = crypto.randomUUID();
		const response: DeltaResponse =
			await firstValueFrom(this.formService.save(state.formId, idempotencyKey));
		// Patch lastSaveResponse BEFORE dispatching ApplyRefDeltas so callers can
		// read the response immediately after the dispatch resolves.
		ctx.patchState({ lastSaveResponse: response });
		ctx.dispatch(new ApplyRefDeltas(response));

		// Post-save: clear the search-pill's loaded-record indicators
		// (state.primaryKeys, which drives loadedLabel) when the legacy ATOM
		// server transitions the form away from the loaded record.
		//
		// Confirmed against legacy Nexus behavior (user-verified 2026-05-21):
		// after Save in BOTH update (Change) and delete modes, the form is
		// reset to default / empty state — no record is "loaded" anymore.
		// The pill label (Type · Name) must clear so the user isn't misled
		// into thinking the now-blank fields still belong to a loaded record.
		// Re-loading is via the search-pill magnifier (lookup browse) which
		// rebuilds primaryKeys at PickRefRecord time.
		if (modeBeforeSave === 'update' || modeBeforeSave === 'delete') {
			ctx.patchState({ primaryKeys: null, recordLabelHint: null });
		}
	}

	@Action(RunRefAppCommand)
	async runRefAppCommand(ctx: StateContext<RefAppStateModel>, action: RunRefAppCommand): Promise<void> {
		const state: RefAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('RunRefAppCommand dispatched with no formId — BootstrapRefForm must run first');
		}
		const idempotencyKey: string = crypto.randomUUID();
		try {
			const response: DeltaResponse =
				await firstValueFrom(this.formService.appCommand(state.formId, action.command, idempotencyKey, action.args));
			ctx.dispatch(new ApplyRefDeltas(response));
			// Business rejection (200 envelope with errorMessage — e.g.
			// updateTruststore keystore-construction failure, ReportBuilder
			// populateMatrices validation) must surface, never
			// apply-and-stay-silent; success statusMessage mirrors the legacy
			// status bar ("Successfully populated matrices.").
			if (response.errorMessage) {
				this.notify.error(response.errorMessage);
			} else if (response.statusMessage) {
				this.notify.success(response.statusMessage);
			}
		} catch (err: unknown) {
			// Transport/HTTP failure — surface, never swallow.
			this.notify.error(`Action failed: ${action.command}`);
			throw err;
		}
	}

	@Action(CloseRefForm)
	async closeRefForm(ctx: StateContext<RefAppStateModel>): Promise<void> {
		// Best-effort eviction via the shared SSoT helper, then reset the edit-form
		// slices regardless of close success.
		await closeFormQuietly(this.formService, ctx.getState().formId, '[CloseRefForm]');
		// Reset edit-form slices but KEEP per-app metadata (appName, appTitle,
		// selfLookupName, filterLookupFlag) — those are per-app, not per-form.
		ctx.patchState({
			formId: null,
			mode: null,
			primaryKeys: null,
			recordLabelHint: null,
			fields: {},
			panes: [],
			actions: [],
			lastSaveResponse: null,
			matrixReloadCounters: {},
			touchSequenceCounter: 0,
		});
	}

	@Action(PickRefRecord)
	async pickRefRecord(ctx: StateContext<RefAppStateModel>, action: PickRefRecord): Promise<void> {
		const primaryKeys: Record<string, string> = {};
		const fields: ReadonlyArray<string> = action.primaryKeyFields;
		const values: ReadonlyArray<string> = action.row.values;
		// Server emits primaryKeyFields[] in matrix-column order; pick parallel
		// values[i] for the same i. When fields and values lengths mismatch the
		// excess is ignored — defensive, but the contract is parallel arrays.
		const len: number = Math.min(fields.length, values.length);
		for (let i: number = 0; i < len; i++) {
			primaryKeys[fields[i]] = values[i];
		}
		const mode: TsmMode = ctx.getState().mode ?? 'inquire';
		// Insert-like modes (insert / addf / addre / import): the server's
		// insert bootstrap IGNORES primaryKeys entirely (defaultAdaptorValues
		// path) — re-bootstrapping would blank the form, wiping the user's
		// typed values, while the pill misleadingly showed the pick. Legacy
		// parity (AppManager.selectEntry): write the picked row's key values
		// onto the LIVE form via the normal field-update/observer path —
		// typed values survive, key fields fill, and no record is "loaded"
		// (Save still inserts), so primaryKeys/pill stay untouched. tenant_cd
		// is excluded — it's implicit request context, not a form field the
		// user sets (legacy lookup result-maps don't carry it either).
		if (isInsertLikeMode(mode)) {
			const pickedFields: Record<string, string> = {};
			for (const [adaptor, value] of Object.entries(primaryKeys)) {
				if (adaptor === TENANT_CD_ADAPTOR) continue;
				pickedFields[adaptor] = value;
			}
			if (Object.keys(pickedFields).length === 0) {
				return;
			}
			await firstValueFrom(ctx.dispatch(new OnRefAdaptorChangeBatch(pickedFields)));
			return;
		}
		// Pill identity override rides the bootstrap action so it lands in
		// state atomically with primaryKeys — see BootstrapRefForm.labelHint.
		await firstValueFrom(ctx.dispatch(new BootstrapRefForm(mode, primaryKeys, action.labelHint ?? null)));
	}

	/**
	 * TMS Navigation — page-state preservation (Phase D). REF mini restore
	 * orchestrator. Mirrors {@code RestoreTsmAppSnapshot} minus the listing and
	 * Add Dialog phases — REF has neither. Re-entrancy-guarded; per-phase
	 * failures isolated with try/catch so partial restore is preferred over
	 * abort.
	 *
	 * <p>{@code restoreInFlight} drives:
	 *   - the REF detail skeleton loader (Phase D.6);
	 *   - matrix-cell-edit disable during restore (impl doc §12.5).
	 *
	 * <p>Pipeline:
	 *   - Phase 3 — detail bootstrap via {@code BootstrapRefForm}.
	 *   - Phase 4 — batched field-values replay.
	 *   - Phase 5 — sequential matrix staged-cell replay.
	 *   - Phase 7 — coordinator broadcast for DOM-bound restoration (scroll).
	 *   - Phase 8 — informational mismatch log for cascade overrides.
	 */
	@Action(RestoreRefAppSnapshot)
	async restoreRefAppSnapshot(
		ctx: StateContext<RefAppStateModel>,
		{ snapshot }: RestoreRefAppSnapshot,
	): Promise<void> {
		// Path B form recovery via the shared SSoT orchestrator. Prefer rebinding
		// the snapshotted formId (the cached BAP holds the user's in-flight values +
		// matrix rows); 410 / non-410 failure falls back to a fresh bootstrap with
		// the standardized toast. mode + primaryKeys are carried through RebindRefForm
		// because ClearRefAppState wiped the record-identity slices the rebind
		// response doesn't return.
		await restoreFormSnapshot({
			hasFormId: !!snapshot.formId,
			isRestoreInFlight: () => ctx.getState().restoreInFlight,
			setRestoreInFlight: (v: boolean) => ctx.patchState({ restoreInFlight: v }),
			// Only an in-progress edit (insert/update/import) loses data on a rebind 410;
			// a nav-back to an inquire view reopens fresh silently. No false expiry toast.
			editAtRisk: isEditAtRiskMode(snapshot.mode),
			rebind: () => firstValueFrom(ctx.dispatch(new RebindRefForm(
				snapshot.formId!,
				snapshot.mode,
				snapshot.primaryKeys ? { ...snapshot.primaryKeys } : null,
				snapshot.recordLabelHint ?? null,
			))),
			bootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapRefForm(
				snapshot.mode,
				snapshot.primaryKeys ?? undefined,
				snapshot.recordLabelHint ?? null,
			))),
			// Phase 7 — DOM restoration broadcast (scrollTop). REF detail subscribes
			// via coordinator.pendingFor('ref').
			onRestored: () => this.coordinator.emit(snapshot),
			notify: this.notify,
			labels: this.labels,
			logTag: '[RestoreRefAppSnapshot]',
		});
	}

}
